import type { AgentKind } from '@cm/protocol';

/**
 * Tema do cockpit: os tokens de cor, os presets e como aplica-los.
 *
 * Como o layout.ts, este modulo e logica pura -- nada de React. A diferenca e
 * que ele precisa tocar o DOM para aplicar o tema, e isso acontece SO dentro do
 * corpo de `applyTheme`. Nada de `document` no escopo do modulo: o runner de
 * teste e `tsx` rodando em Node, onde `document` e `localStorage` nao existem,
 * e um acesso no topo do arquivo quebraria o import antes de qualquer teste
 * rodar. Pela mesma razao a validacao mora em `parseTheme`, que e pura e
 * testavel, e `loadTheme` so a embrulha com o acesso ao storage.
 */

export type ThemeMode = 'dark' | 'light';

/**
 * Um token por custom property do `:root`.
 *
 * As chaves viram nomes CSS por `cssVarName` (camelCase -> kebab-case), com
 * duas excecoes de nome historico tratadas la. Acrescentar um token aqui e
 * suficiente para ele ser aplicado: `applyTheme` itera sobre as chaves.
 */
export interface ThemeTokens {
  bg: string;
  bgDeep: string;
  surface: string;
  surfaceRaised: string;
  surfaceHover: string;
  border: string;
  borderStrong: string;

  text: string;
  textDim: string;
  textFaint: string;

  accent: string;
  accentBright: string;
  accentDeep: string;
  /** Texto sobre o acento. `deriveOnAccent` calcula por luminancia. */
  onAccent: string;

  ok: string;
  warn: string;
  warnStrong: string;
  danger: string;

  shadow: string;
  scrim: string;

  /**
   * Identidade por agente. Precisa ser por tema, nao constante: o prata do
   * Codex (`#c7c2b8`) e legivel sobre neutro escuro e praticamente invisivel
   * sobre um fundo claro.
   */
  agentClaude: string;
  agentCodex: string;
  agentGemini: string;
  agentGrok: string;
}

/** As 16 cores ANSI do terminal. Fundo, frente e cursor saem dos tokens. */
export interface AnsiTheme {
  black: string;
  red: string;
  green: string;
  yellow: string;
  blue: string;
  magenta: string;
  cyan: string;
  white: string;
  brightBlack: string;
  brightRed: string;
  brightGreen: string;
  brightYellow: string;
  brightBlue: string;
  brightMagenta: string;
  brightCyan: string;
  brightWhite: string;
}

export interface Theme {
  id: string;
  label: string;
  /** Dita o `color-scheme` e a direcao das derivacoes de neutros. */
  mode: ThemeMode;
  tokens: ThemeTokens;
  ansi: AnsiTheme;
}

export const STORAGE_KEY = 'cm.theme';
export const DEFAULT_THEME_ID = 'coral';

/** Ordem em que o painel de Aparencia lista os campos do ajuste fino. */
export const PRIMARY_TOKENS: { key: keyof ThemeTokens; label: string }[] = [
  { key: 'bg', label: 'Fundo' },
  { key: 'surface', label: 'Superfície' },
  { key: 'border', label: 'Borda' },
  { key: 'text', label: 'Texto' },
  { key: 'accent', label: 'Acento' },
];

/** Os demais tokens, atras do "avançado". */
export const ADVANCED_TOKENS: { key: keyof ThemeTokens; label: string }[] = [
  { key: 'bgDeep', label: 'Fundo do terminal' },
  { key: 'surfaceRaised', label: 'Superfície elevada' },
  { key: 'surfaceHover', label: 'Superfície em hover' },
  { key: 'borderStrong', label: 'Borda forte' },
  { key: 'textDim', label: 'Texto secundário' },
  { key: 'textFaint', label: 'Texto apagado' },
  { key: 'accentBright', label: 'Acento claro' },
  { key: 'accentDeep', label: 'Acento profundo' },
  { key: 'onAccent', label: 'Texto sobre o acento' },
  { key: 'ok', label: 'Sucesso' },
  { key: 'warn', label: 'Aviso' },
  { key: 'warnStrong', label: 'Aviso forte' },
  { key: 'danger', label: 'Perigo' },
  { key: 'agentClaude', label: 'Claude' },
  { key: 'agentCodex', label: 'Codex' },
  { key: 'agentGemini', label: 'Gemini' },
  { key: 'agentGrok', label: 'Grok Build' },
];

// ---------------------------------------------------------------------------
// Cor: parsing e mistura
// ---------------------------------------------------------------------------

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function isHex(value: unknown): value is string {
  return typeof value === 'string' && HEX_RE.test(value.trim());
}

/** `#abc` e `#aabbcc` -> [r, g, b] em 0-255. */
function toRgb(hex: string): [number, number, number] {
  let h = hex.trim().slice(1);
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(rgb: [number, number, number]): string {
  return (
    '#' +
    rgb
      .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
      .join('')
  );
}

/**
 * Mistura duas cores. `ratio` 0 devolve `base`, 1 devolve `over`.
 *
 * Existe porque o xterm aceita hex puro e nao entende `color-mix`. No CSS a
 * mistura fica declarativa; aqui ela tem que ser calculada.
 */
export function blendHex(base: string, over: string, ratio: number): string {
  if (!isHex(base)) return isHex(over) ? over : '#000000';
  if (!isHex(over)) return base;
  const t = Math.max(0, Math.min(1, ratio));
  const a = toRgb(base);
  const b = toRgb(over);
  return toHex([
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ]);
}

/** Luminancia relativa da WCAG, usada para decidir contraste. */
export function luminance(hex: string): number {
  if (!isHex(hex)) return 0;
  const [r, g, b] = toRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Razao de contraste da WCAG entre duas cores (1 a 21). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Escolhe o texto que vai sobre o acento.
 *
 * E o unico token que nao da para derivar por polaridade do tema: depende do
 * contraste do acento em si. Um acento amarelo pede texto escuro tanto num tema
 * escuro quanto num claro. Por isso a decisao e por luminancia, e nao por
 * `mode` -- assim o botao primario nunca fica com texto ilegivel, seja qual for
 * a cor que o usuario escolher no ajuste fino.
 */
export function deriveOnAccent(accent: string, tokens: ThemeTokens): string {
  const dark = blendHex(accent, '#000000', 0.86);
  const light = blendHex(accent, '#ffffff', 0.94);
  return contrastRatio(accent, dark) >= contrastRatio(accent, light) ? dark : light;
}

// ---------------------------------------------------------------------------
// Derivacao da rampa de neutros
// ---------------------------------------------------------------------------

/**
 * Regenera os tokens secundarios a partir dos cinco primarios
 * (`bg`, `surface`, `border`, `text`, `accent`) e das cores de estado.
 *
 * E o que sustenta o ajuste fino: o usuario mexe em cinco cores e recebe uma
 * rampa coerente, em vez de precisar acertar os vinte e poucos tokens na mao.
 * Os primarios e as cores de estado sao preservados como vieram.
 */
export function deriveNeutrals(tokens: ThemeTokens, mode: ThemeMode): ThemeTokens {
  const { bg, surface, border, text, accent } = tokens;
  const light = mode === 'light';

  // Num tema escuro a area "funda" (o terminal) afunda para o preto; num claro
  // ela sobe para o branco, que e o que se espera de uma tela de conteudo
  // sobre um chrome acinzentado.
  const bgDeep = light ? blendHex(bg, '#ffffff', 0.72) : blendHex(bg, '#000000', 0.42);

  // Superficie elevada: no escuro se aproxima do texto (clareia); no claro sobe
  // para o branco. Nas duas a peca elevada fica mais clara que o fundo dela.
  const raise = (ratio: number) =>
    light ? blendHex(surface, '#ffffff', ratio * 6) : blendHex(surface, text, ratio);

  return {
    ...tokens,
    bgDeep,
    surfaceRaised: raise(0.05),
    surfaceHover: raise(0.1),
    // Mais contraste que a borda base, nas duas polaridades.
    borderStrong: blendHex(border, text, 0.17),
    // Menos contraste que o texto base: misturar com o fundo serve nas duas.
    textDim: blendHex(text, bg, 0.34),
    textFaint: blendHex(text, bg, 0.58),
    // Puxar para o texto clareia no escuro e escurece no claro -- em ambos o
    // resultado e "a mesma cor, com mais presenca".
    accentBright: blendHex(accent, text, 0.2),
    accentDeep: blendHex(accent, bg, 0.82),
    onAccent: deriveOnAccent(accent, tokens),
  };
}

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

/** ANSI quente, na paleta dos temas escuros de neutro âmbar. */
const ANSI_WARM_DARK: AnsiTheme = {
  black: '#171614',
  red: '#d9615a',
  green: '#7fb069',
  yellow: '#e0a458',
  blue: '#7b9bf5',
  magenta: '#b98cd4',
  cyan: '#5aa9a3',
  white: '#ede8e0',
  brightBlack: '#6e675e',
  brightRed: '#e88781',
  brightGreen: '#9bc98a',
  brightYellow: '#edc27f',
  brightBlue: '#9db6f8',
  brightMagenta: '#cfaae3',
  brightCyan: '#84c4bf',
  brightWhite: '#fbf7f1',
};

const ANSI_COOL_DARK: AnsiTheme = {
  ...ANSI_WARM_DARK,
  black: '#151719',
  white: '#e2e6ea',
  brightBlack: '#646d76',
  brightWhite: '#f4f7f9',
};

/**
 * ANSI para fundo claro. Nao da para reaproveitar a versao escura: as cores
 * "bright" dela sao pastel e desaparecem sobre branco, e o `white` viraria
 * texto branco em papel branco.
 */
const ANSI_LIGHT: AnsiTheme = {
  black: '#2b2724',
  red: '#b03a34',
  green: '#4a7a36',
  yellow: '#9a6b18',
  blue: '#3a5fc0',
  magenta: '#8244a8',
  cyan: '#26706a',
  white: '#5f5850',
  brightBlack: '#8a8279',
  brightRed: '#cc4f47',
  brightGreen: '#5c9245',
  brightYellow: '#b58424',
  brightBlue: '#4f74d4',
  brightMagenta: '#9a58bd',
  brightCyan: '#31857e',
  brightWhite: '#2b2724',
};

const CORAL: Theme = {
  id: 'coral',
  label: 'Coral',
  mode: 'dark',
  tokens: {
    bg: '#171614',
    bgDeep: '#100f0e',
    surface: '#1e1c1a',
    surfaceRaised: '#262320',
    surfaceHover: '#2e2a26',
    border: '#322e29',
    borderStrong: '#46403a',
    text: '#ede8e0',
    textDim: '#a8a096',
    textFaint: '#6e675e',
    accent: '#d97757',
    accentBright: '#e88c6d',
    accentDeep: '#3a2319',
    onAccent: '#1a120d',
    ok: '#7fb069',
    warn: '#e0a458',
    warnStrong: '#f09455',
    danger: '#d9615a',
    shadow: 'rgba(0, 0, 0, 0.55)',
    scrim: 'rgba(8, 7, 6, 0.72)',
    agentClaude: '#d97757',
    agentCodex: '#c7c2b8',
    agentGemini: '#7b9bf5',
    agentGrok: '#f2a900',
  },
  ansi: ANSI_WARM_DARK,
};

const ARDOSIA: Theme = {
  id: 'ardosia',
  label: 'Ardósia',
  mode: 'dark',
  tokens: {
    bg: '#15171a',
    bgDeep: '#0e1013',
    surface: '#1b1e22',
    surfaceRaised: '#23272c',
    surfaceHover: '#2b3036',
    border: '#2e343a',
    borderStrong: '#434b53',
    text: '#e2e6ea',
    textDim: '#9aa3ac',
    textFaint: '#646d76',
    accent: '#5b9dd9',
    accentBright: '#7fb6e8',
    accentDeep: '#16283a',
    onAccent: '#08131c',
    ok: '#6fb08a',
    warn: '#d9a95c',
    warnStrong: '#e8894f',
    danger: '#d4645f',
    shadow: 'rgba(0, 0, 0, 0.6)',
    scrim: 'rgba(6, 8, 10, 0.74)',
    agentClaude: '#e08a63',
    agentCodex: '#aab4bd',
    agentGemini: '#7b9bf5',
    agentGrok: '#d99400',
  },
  ansi: ANSI_COOL_DARK,
};

const CLARO: Theme = {
  id: 'claro',
  label: 'Claro',
  mode: 'light',
  tokens: {
    bg: '#eae6df',
    bgDeep: '#fbf9f5',
    surface: '#f4f1eb',
    surfaceRaised: '#fbf9f6',
    surfaceHover: '#ffffff',
    border: '#d5cec3',
    borderStrong: '#b3a99b',
    text: '#2b2724',
    textDim: '#5f5850',
    textFaint: '#8a8279',
    // Terracota mais fundo que o do tema escuro, e nao o mesmo coral: o botao
    // primario e preenchido com o acento e precisa de texto legivel em cima.
    // Um coral de meio-tom e o pior caso -- nao contrasta nem com branco nem
    // com preto. Este da 5.3:1 com o texto claro abaixo.
    accent: '#a94b2b',
    // Num tema claro "mais presente" quer dizer mais escuro, nao mais claro.
    accentBright: '#8f3d20',
    accentDeep: '#f2ddd3',
    onAccent: '#fdf6f2',
    ok: '#4a7a36',
    warn: '#9a6b18',
    warnStrong: '#c05f1c',
    danger: '#b03a34',
    shadow: 'rgba(58, 48, 38, 0.18)',
    scrim: 'rgba(43, 39, 36, 0.4)',
    // O prata do Codex desaparece sobre papel; aqui ele vira um cinza escuro.
    agentClaude: '#a94b2b',
    agentCodex: '#6d665c',
    agentGemini: '#3a5fc0',
    agentGrok: '#a86f00',
  },
  ansi: ANSI_LIGHT,
};

const CONTRASTE: Theme = {
  id: 'contraste',
  label: 'Alto contraste',
  mode: 'dark',
  tokens: {
    bg: '#000000',
    bgDeep: '#000000',
    surface: '#0d0d0d',
    surfaceRaised: '#1a1a1a',
    surfaceHover: '#262626',
    border: '#5a5a5a',
    borderStrong: '#8f8f8f',
    text: '#ffffff',
    textDim: '#d0d0d0',
    textFaint: '#a0a0a0',
    accent: '#ff9166',
    accentBright: '#ffb494',
    accentDeep: '#4a2314',
    onAccent: '#1a0a02',
    ok: '#6ede8a',
    warn: '#ffd166',
    warnStrong: '#ffa04d',
    danger: '#ff8074',
    shadow: 'rgba(0, 0, 0, 0.85)',
    scrim: 'rgba(0, 0, 0, 0.85)',
    agentClaude: '#ff9166',
    agentCodex: '#e0dcd4',
    agentGemini: '#9db6f8',
    agentGrok: '#ffc233',
  },
  ansi: {
    ...ANSI_WARM_DARK,
    black: '#000000',
    red: '#ff8074',
    green: '#6ede8a',
    yellow: '#ffd166',
    blue: '#9db6f8',
    white: '#ffffff',
    brightBlack: '#a0a0a0',
    brightWhite: '#ffffff',
  },
};

export const PRESETS: Theme[] = [CORAL, ARDOSIA, CLARO, CONTRASTE];

export function presetById(id: string): Theme | undefined {
  return PRESETS.find((t) => t.id === id);
}

export function defaultTheme(): Theme {
  return cloneTheme(CORAL);
}

function cloneTheme(theme: Theme): Theme {
  return {
    ...theme,
    tokens: { ...theme.tokens },
    ansi: { ...theme.ansi },
  };
}

// ---------------------------------------------------------------------------
// Validacao e persistencia
// ---------------------------------------------------------------------------

/**
 * Normaliza qualquer coisa vinda do storage num tema utilizavel.
 *
 * Pura de proposito: e o que permite testar a validacao sem `localStorage`.
 * Postura igual a do `loadLayout` -- tema e conveniencia e nunca pode impedir o
 * cockpit de abrir, entao campo invalido cai no valor do preset base em vez de
 * lancar. Token faltando tambem: um `cm.theme` gravado por uma versao anterior
 * do app, sem um token que passou a existir depois, tem que continuar abrindo.
 */
export function parseTheme(raw: unknown): Theme {
  if (!raw || typeof raw !== 'object') return defaultTheme();
  const obj = raw as Record<string, unknown>;

  const id = typeof obj.id === 'string' && obj.id.trim() ? obj.id.trim().slice(0, 40) : DEFAULT_THEME_ID;
  // Um tema custom guarda de qual preset ele saiu, para herdar o que nao foi
  // editado (o bloco ANSI, principalmente).
  const baseId = typeof obj.baseId === 'string' ? obj.baseId : id;
  const base = presetById(baseId) ?? presetById(id) ?? CORAL;

  const mode: ThemeMode = obj.mode === 'light' || obj.mode === 'dark' ? obj.mode : base.mode;
  const label =
    typeof obj.label === 'string' && obj.label.trim()
      ? obj.label.trim().slice(0, 40)
      : base.label;

  const tokens = { ...base.tokens };
  const rawTokens = obj.tokens;
  if (rawTokens && typeof rawTokens === 'object') {
    for (const key of Object.keys(tokens) as (keyof ThemeTokens)[]) {
      const value = (rawTokens as Record<string, unknown>)[key];
      // shadow e scrim sao rgba(), nao hex -- validados separadamente.
      if (key === 'shadow' || key === 'scrim') {
        if (typeof value === 'string' && isColorFunction(value)) tokens[key] = value;
      } else if (isHex(value)) {
        tokens[key] = normalizeHex(value);
      }
    }
  }

  const ansi = { ...base.ansi };
  const rawAnsi = obj.ansi;
  if (rawAnsi && typeof rawAnsi === 'object') {
    for (const key of Object.keys(ansi) as (keyof AnsiTheme)[]) {
      const value = (rawAnsi as Record<string, unknown>)[key];
      if (isHex(value)) ansi[key] = normalizeHex(value);
    }
  }

  return { id, label, mode, tokens, ansi };
}

/** Aceita as poucas formas funcionais que os tokens de profundidade usam. */
function isColorFunction(value: string): boolean {
  return /^(?:rgba?|hsla?|color-mix)\([^;{}]*\)$/i.test(value.trim());
}

/** `#ABC` -> `#aabbcc`, para comparar temas por igualdade de string. */
export function normalizeHex(value: string): string {
  let h = value.trim().toLowerCase().slice(1);
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return '#' + h;
}

export function loadTheme(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultTheme();
    return parseTheme(JSON.parse(raw));
  } catch {
    // Storage indisponivel (modo privado), JSON corrompido, cota cheia: o
    // cockpit abre no tema padrao e segue funcionando.
    return defaultTheme();
  }
}

/**
 * O mapa final "--nome-css" -> valor.
 *
 * Gravado junto do tema para o script pre-paint do index.html poder repintar
 * sem conhecer os presets nem a regra de camelCase -> kebab-case. Sem isso o
 * HTML teria uma segunda copia da lista de tokens, que sairia de sincronia na
 * primeira vez que alguem acrescentasse um.
 */
export function themeCssVars(theme: Theme): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of Object.keys(theme.tokens) as (keyof ThemeTokens)[]) {
    out[cssVarName(key)] = theme.tokens[key];
  }
  return out;
}

export function saveTheme(theme: Theme): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...theme, cssVars: themeCssVars(theme), mode: theme.mode }),
    );
  } catch {
    /* sem persistencia; o tema vale so para esta aba */
  }
}

// ---------------------------------------------------------------------------
// Aplicacao no documento
// ---------------------------------------------------------------------------

/**
 * `agentClaude` -> `--claude`, `bgDeep` -> `--bg-deep`.
 *
 * As cores de agente sao excecao porque os nomes CSS (`--claude`, `--codex`,
 * `--gemini`, `--grok`) vieram antes de existir um tema e sao usados em regras
 * espalhadas; renomea-los seria churn sem ganho.
 */
export function cssVarName(key: keyof ThemeTokens): string {
  if (key === 'agentClaude') return '--claude';
  if (key === 'agentCodex') return '--codex';
  if (key === 'agentGemini') return '--gemini';
  if (key === 'agentGrok') return '--grok';
  return '--' + String(key).replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
}

/**
 * Escreve os tokens no proprio `<html>`.
 *
 * Inline no elemento raiz, e nao numa folha de estilo injetada, porque o
 * `:root` do styles.css ja define os mesmos nomes: uma declaracao inline ganha
 * de qualquer regra por especificidade, sem depender de ordem de insercao.
 */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  for (const key of Object.keys(theme.tokens) as (keyof ThemeTokens)[]) {
    root.style.setProperty(cssVarName(key), theme.tokens[key]);
  }

  // Sem isto os controles nativos -- os <select> de autonomia e de colunas, o
  // checkbox da sidebar, as barras de rolagem -- continuam desenhados escuros
  // sobre um tema claro. O <meta name="color-scheme"> do index.html vale so
  // para o primeiro paint; daqui para frente quem manda e o tema.
  root.style.colorScheme = theme.mode;
  root.dataset.theme = theme.id;
  root.dataset.themeMode = theme.mode;
}

/** A cor de identidade de um agente, dentro do tema atual. */
export function agentColor(tokens: ThemeTokens, agent: AgentKind): string {
  if (agent === 'codex') return tokens.agentCodex;
  if (agent === 'gemini') return tokens.agentGemini;
  if (agent === 'grok') return tokens.agentGrok;
  return tokens.agentClaude;
}

/**
 * Tema do xterm para um pane.
 *
 * `paneColor` tinge o fundo e pinta o cursor, para dois panes lado a lado se
 * distinguirem sem precisar ler o cabecalho. 6% e o teto pratico: o suficiente
 * para a diferenca ser visivel de longe e pouco o bastante para nao mexer no
 * contraste do que o TUI desenha. O xterm exige hex puro, entao a mistura vem
 * do `blendHex` -- `color-mix` nao serve aqui.
 */
export function terminalTheme(theme: Theme, paneColor: string) {
  const bg = blendHex(theme.tokens.bgDeep, paneColor, 0.06);
  return {
    ...theme.ansi,
    background: bg,
    foreground: theme.tokens.text,
    cursor: paneColor,
    cursorAccent: bg,
    selectionBackground: blendHex(theme.tokens.bgDeep, paneColor, 0.3),
  };
}

// ---------------------------------------------------------------------------
// Tamanho de fonte
// ---------------------------------------------------------------------------

/**
 * Preferencias de tamanho de fonte.
 *
 * Separadas do tema (chave propria no storage) porque sao ortogonais: trocar de
 * tema nao deveria devolver a fonte ao padrao, e quem aumenta a fonte por
 * acuidade visual quer isso valendo em qualquer paleta.
 */
export interface FontPrefs {
  /** Multiplicador do chrome (sidebar, topbar, paineis, cabecalho do pane). */
  ui: number;
  /** Tamanho base do terminal, em px, quando ha espaco de sobra. */
  termBase: number;
  /** Encolher a fonte do pane estreito para caber TARGET_COLS colunas. */
  termAuto: boolean;
  /** Piso do auto-ajuste. Legibilidade ganha de quantidade de colunas. */
  termMin: number;
}

export const FONTS_STORAGE_KEY = 'cm.fonts';

/**
 * A largura para a qual os TUIs dos agentes sao desenhados.
 *
 * Claude Code, Codex e Gemini assumem um terminal de 80 colunas. Abaixo disso
 * eles quebram linha, repetem moldura e desperdicam altura -- o pane parece
 * "mostrar pouca coisa" mesmo com a fonte grande. Encolher a fonte ate caber 80
 * colunas mostra MAIS conteudo, nao menos, e e por isso que o auto-ajuste mira
 * um numero de colunas em vez de um tamanho de fonte.
 */
export const TARGET_COLS = 80;

export const DEFAULT_FONTS: FontPrefs = {
  ui: 1,
  termBase: 12,
  termAuto: true,
  termMin: 8,
};

export const UI_SCALE_MIN = 0.8;
export const UI_SCALE_MAX = 1.5;
export const TERM_BASE_MIN = 8;
export const TERM_BASE_MAX = 22;
/** Piso absoluto: abaixo disto o antialias come o glifo e nada se le. */
export const TERM_FLOOR = 6;

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback;
}

/** Pura, como o parseTheme: e o que permite testar sem localStorage. */
export function parseFontPrefs(raw: unknown): FontPrefs {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_FONTS };
  const o = raw as Record<string, unknown>;
  const termBase = clampNumber(o.termBase, TERM_BASE_MIN, TERM_BASE_MAX, DEFAULT_FONTS.termBase);
  return {
    ui: clampNumber(o.ui, UI_SCALE_MIN, UI_SCALE_MAX, DEFAULT_FONTS.ui),
    termBase,
    termAuto: typeof o.termAuto === 'boolean' ? o.termAuto : DEFAULT_FONTS.termAuto,
    // O piso nunca pode passar do base, senao o "auto" aumentaria a fonte de um
    // pane apertado -- o oposto do que ele existe para fazer.
    termMin: Math.min(
      termBase,
      clampNumber(o.termMin, TERM_FLOOR, TERM_BASE_MAX, DEFAULT_FONTS.termMin),
    ),
  };
}

export function loadFontPrefs(): FontPrefs {
  try {
    const raw = localStorage.getItem(FONTS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_FONTS };
    return parseFontPrefs(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_FONTS };
  }
}

export function saveFontPrefs(prefs: FontPrefs): void {
  try {
    localStorage.setItem(FONTS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* sem persistencia; vale so para esta aba */
  }
}

export function applyFontPrefs(prefs: FontPrefs): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--ui-scale', String(prefs.ui));
}

/**
 * Qual fonte o terminal deste pane deve usar.
 *
 * Recebe quantas colunas o xterm mediu COM `currentSize` aplicado, e nao a
 * largura em pixels: a largura da celula depende da fonte, do zoom do
 * navegador e do device pixel ratio. Deixar o proprio xterm medir e regra de
 * tres em cima disso dispensa constante magica e funciona em qualquer DPI.
 *
 * Como a largura da celula e proporcional ao tamanho da fonte, colunas sao
 * inversamente proporcionais: `cols * size` e invariante.
 */
export function fitFontSize(
  prefs: FontPrefs,
  currentSize: number,
  measuredCols: number,
  override?: number,
): number {
  const base = override ?? prefs.termBase;
  if (!prefs.termAuto || override !== undefined) return base;
  if (!Number.isFinite(measuredCols) || measuredCols <= 0 || currentSize <= 0) return base;

  const ideal = (currentSize * measuredCols) / TARGET_COLS;
  // Nunca aumenta acima do base: o auto existe para caber mais, nao para
  // esticar a fonte num pane largo.
  const wanted = Math.min(base, ideal);
  // Meio pixel e o menor passo que o xterm distingue de forma estavel, e o
  // arredondamento e para BAIXO de proposito: para cima erra a meta. Com 66
  // colunas a 12px o ideal e 9.9px; arredondar para 10px deixa 79 colunas --
  // uma a menos do que todo o mecanismo existe para alcancar.
  const rounded = Math.floor(wanted * 2) / 2;
  return Math.max(prefs.termMin, Math.min(base, rounded));
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

export const SIDEBAR_STORAGE_KEY = 'cm.sidebar';

/**
 * Atraso antes de abrir no hover.
 *
 * Sem ele a sidebar abre toda vez que o ponteiro atravessa a faixa a caminho de
 * outra coisa. Fechar nao tem atraso: sair e uma intencao clara, e segurar a
 * sobreposicao aberta esconde o terminal de baixo.
 */
export const SIDEBAR_HOVER_DELAY_MS = 200;

export function loadSidebarPinned(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'pinned';
  } catch {
    return false;
  }
}

export function saveSidebarPinned(pinned: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, pinned ? 'pinned' : 'auto');
  } catch {
    /* sem persistencia; vale so para esta aba */
  }
}
